import { FC } from 'react';
import { OAuthProviderType, LinkedProvider } from '../../types/account.types';

interface ProviderCardProps {
  provider: LinkedProvider;
  canUnlink: boolean;
  onUnlink: () => void;
  onSetPrimary: () => void;
}

const getProviderInfo = (type: OAuthProviderType) => {
  switch (type) {
    case 'GOOGLE':
      return { name: 'Google', color: 'bg-red-500' };
    case 'FACEBOOK':
      return { name: 'Facebook', color: 'bg-blue-600' };
    case 'EMAIL_PASSWORD':
      return { name: 'Email & Password', color: 'bg-gray-600' };
    default:
      return { name: type, color: 'bg-gray-500' };
  }
};

// Provider-specific icons
function ProviderIcon({ provider }: { provider: OAuthProviderType }) {
  if (provider === 'GOOGLE') {
    return (
      <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="#ffffff"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#ffffff"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#ffffff"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#ffffff"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    );
  }

  if (provider === 'FACEBOOK') {
    return (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    );
  }

  // Email/Password icon
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

export const ProviderCard: FC<ProviderCardProps> = ({
  provider,
  canUnlink,
  onUnlink,
  onSetPrimary,
}) => {
  const info = getProviderInfo(provider.provider);
  const linkedDate = new Date(provider.linkedAt).toLocaleDateString();

  return (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors bg-white">
      <div className="flex items-center space-x-4">
        <div className={`${info.color} text-white p-3 rounded-lg flex items-center justify-center`}>
          <ProviderIcon provider={provider.provider} />
        </div>
        <div>
          <div className="flex items-center space-x-2">
            <h3 className="font-semibold text-gray-900">{info.name}</h3>
            {provider.isPrimary && (
              <span className="px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                Primary
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">{provider.email}</p>
          <p className="text-xs text-gray-400">Linked on {linkedDate}</p>
        </div>
      </div>

      <div className="flex items-center space-x-3">
        {!provider.isPrimary && (
          <button
            type="button"
            onClick={onSetPrimary}
            className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Set as Primary
          </button>
        )}
        <button
          type="button"
          onClick={onUnlink}
          disabled={!canUnlink}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            canUnlink
              ? 'text-red-600 border border-red-600 hover:bg-red-50'
              : 'text-gray-400 border border-gray-300 cursor-not-allowed'
          }`}
          title={!canUnlink ? 'Cannot unlink your only authentication method' : undefined}
        >
          Unlink
        </button>
      </div>
    </div>
  );
};
